/**
 * Print template generators for waste checksheet.
 *
 * Instead of scraping DOM innerHTML, these functions build clean HTML
 * directly from data. This makes the print output independent of the
 * screen UI and easy to customise per mode (legacy vs UOM).
 */
import { Category } from '../interfaces/Category';
import { ItemWasteChecksheetData, WasteLogReason } from '../interfaces/ItemWasteChecksheetData';
import { WasteItemUomInfo } from '../../../app/services/api/generated';
import { formatConversions } from '../utils/uomConversionUtils';
import { legacyPrintStyles, uomPrintStyles } from './printStyles';

/** Escape HTML special characters to prevent injection. */
const esc = (str: string): string =>
  str.replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

export interface PrintTranslations {
  itemNumber: string;
  itemDescription: string;
  lowestUom: string;
  uomConversions: string;
  total: string;
}

export interface PrintOptions {
  categories: Category[];
  activeReasons: WasteLogReason[];
  itemConversions: Record<string, WasteItemUomInfo[]>;
  translations: PrintTranslations;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Single empty input box for handwriting quantities / tally marks. */
const inputBox = (): string => '<div class="input-box"></div>';

/** UOM tick boxes – one checkbox per available UOM so the user can mark which unit. */
const uomTickBoxes = (item: ItemWasteChecksheetData): string => {
  const uoms = item.availableUoms;
  if (!uoms || uoms.length === 0) return '';
  return `<div class="uom-ticks">${
    uoms.map((u) => `<label class="uom-tick"><span class="tick-box"></span>${esc(u.code)}</label>`).join('')
  }</div>`;
};

// ---------------------------------------------------------------------------
// Legacy template (WasteLogUnitSupport OFF)
// ---------------------------------------------------------------------------

/**
 * Generates print HTML for the legacy mode.
 * Columns: Item #, Description, UOM, [Reason write-areas...], Total.
 */
const buildLegacyPrintBody = (options: PrintOptions): string => {
  const { categories, activeReasons, translations } = options;

  const reasonCount = activeReasons.length;
  const reasonWidth = reasonCount > 0 ? Math.floor(50 / reasonCount) : 12;

  let html = '';
  for (const category of categories) {
    html += '<div class="category-section">';
    html += `<div class="category-title">${esc(category.name)}</div>`;
    html += '<table>';
    html += `<colgroup>
      <col style="width:12%">
      <col style="width:20%">
      <col style="width:8%">
      ${activeReasons.map(() => `<col style="width:${reasonWidth}%">`).join('')}
      <col style="width:10%">
    </colgroup>`;
    html += `<thead><tr>
      <th>${esc(translations.itemNumber)}</th>
      <th>${esc(translations.itemDescription)}</th>
      <th>${esc(translations.lowestUom)}</th>
      ${activeReasons.map((r) => `<th>${esc(r.name)}</th>`).join('')}
      <th>${esc(translations.total)}</th>
    </tr></thead>`;
    html += '<tbody>';

    for (const item of category.items) {
      html += '<tr>';
      html += `<td class="item-number">${esc(item.itemNumber ?? '')}</td>`;
      html += `<td class="item-description">${esc(item.description ?? '')}</td>`;
      html += `<td class="uom-info">${esc(item.uom ?? '')}</td>`;
      for (let i = 0; i < activeReasons.length; i++) {
        html += `<td>${inputBox()}</td>`;
      }
      html += `<td>${inputBox()}</td>`;
      html += '</tr>';
    }

    html += '</tbody></table></div>';
  }
  return html;
};

// ---------------------------------------------------------------------------
// UOM-aware template (WasteLogUnitSupport ON)
// ---------------------------------------------------------------------------

/**
 * Generates print HTML for the UOM-aware mode.
 * Columns: Item #, Description, Conversions, [Reason write-areas + UOM ticks...].
 * Total Qty and Total Cost are intentionally omitted to maximise writing space.
 */
const buildUomPrintBody = (options: PrintOptions): string => {
  const { categories, activeReasons, itemConversions, translations } = options;

  const reasonCount = activeReasons.length;
  const reasonWidth = reasonCount > 0 ? Math.floor(56 / reasonCount) : 14;

  let html = '';
  for (const category of categories) {
    html += '<div class="category-section">';
    html += `<div class="category-title">${esc(category.name)}</div>`;
    html += '<table>';
    html += `<colgroup>
      <col style="width:11%">
      <col style="width:17%">
      <col style="width:16%">
      ${activeReasons.map(() => `<col style="width:${reasonWidth}%">`).join('')}
    </colgroup>`;
    html += `<thead><tr>
      <th>${esc(translations.itemNumber)}</th>
      <th>${esc(translations.itemDescription)}</th>
      <th>${esc(translations.uomConversions)}</th>
      ${activeReasons.map((r) => `<th>${esc(r.name)}</th>`).join('')}
    </tr></thead>`;
    html += '<tbody>';

    for (const item of category.items) {
      const convStr = formatConversions(item.uom ?? '', itemConversions[item.itemNumber ?? '']);
      html += '<tr>';
      html += `<td class="item-number">${esc(item.itemNumber ?? '')}</td>`;
      html += `<td class="item-description">${esc(item.description ?? '')}</td>`;
      html += `<td class="uom-info">${esc(convStr).replace(/\n/g, '<br>')}</td>`;
      for (let i = 0; i < activeReasons.length; i++) {
        html += `<td class="reason-cell">${inputBox()}${uomTickBoxes(item)}</td>`;
      }
      html += '</tr>';
    }

    html += '</tbody></table></div>';
  }
  return html;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Build a complete, self-contained HTML document ready for window.print().
 */
export const buildPrintDocument = (
  options: PrintOptions & { isUomMode: boolean },
): string => {
  const { isUomMode, ...printOptions } = options;
  const styles = isUomMode ? uomPrintStyles : legacyPrintStyles;
  const body = isUomMode
    ? buildUomPrintBody(printOptions)
    : buildLegacyPrintBody(printOptions);

  return `<!DOCTYPE html>
<html>
<head><title>Waste Log</title><style>${styles}</style></head>
<body>${body}</body>
</html>`;
};
